// =====================================================================
// GlobalSearch — command-palette style navigation search, triggered from
// the navbar's search field (Header.jsx) and the app-wide Cmd/Ctrl+K
// shortcut (useGlobalSearch). Indexes both the app's own navigable
// destinations (sidebar nav + Settings workspace pages) AND real entities —
// districts/sectors, models/notebooks, active alerts, datasets/tables —
// each permission-filtered with the exact same hasAnyPermission rule, so
// search can never surface a result the caller would then get a 403 on.
// See utils/globalSearchLogic.mjs for the pure index/match/group logic and
// hooks/useGlobalSearch.js's useGlobalSearchEntities for the debounced,
// cached entity fetch.
//
// Built on the shared <Modal> (portal + focus trap + Escape-to-close +
// scroll-lock — see components/ui/Modal.jsx), so this component only owns
// the search-specific bits: the input, arrow-key/Enter navigation through
// results, and the four states (empty query / no results / loading /
// results).
// =====================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  ArrowRight,
  LayoutDashboard,
  BellRing,
  Sliders,
  Brain,
  Upload,
  HardDrive,
  Database,
  Table,
  GitBranch,
  BarChart3,
  TrendingUp,
  Bookmark,
  Shield,
  Settings,
  User,
  FileCode,
  Image as ImageIcon,
  Users as UsersIcon,
  MapPin,
  Bell,
  Mail,
  Loader2,
  AlertTriangle,
} from 'lucide-react'
import Modal from './ui/Modal'
import { useAuth } from '../contexts/AuthContext'
import { cn } from '../utils/cn'
import { hasAnyPermission } from '../utils/permissions.js'
import {
  buildSearchIndex,
  buildEntityIndex,
  searchCombined,
  groupSearchResults,
  ENTITY_REQUIRE_ANY,
} from '../utils/globalSearchLogic.mjs'
import { useGlobalSearchEntities } from '../hooks/useGlobalSearch'

// Every icon key that can appear in globalSearchLogic's destination
// catalogue (nav item icons from sidebarNav.mjs + settings page icons from
// pages/settings/sections.js + the 'User' account-route icon). Falls back
// to the generic Search glyph for anything unmapped rather than crashing.
const ICONS = {
  LayoutDashboard,
  BellRing,
  Sliders,
  Brain,
  Upload,
  HardDrive,
  Search,
  Database,
  Table,
  GitBranch,
  BarChart3,
  TrendingUp,
  Bookmark,
  Shield,
  Settings,
  User,
  FileCode,
  ImageIcon,
  UsersIcon,
  MapPin,
  Bell,
  Mail,
}

const QUICK_LINKS_LIMIT = 6

// Resting-state (non-active-row) avatar tint per result type, using the
// same design tokens (tailwind.config.js primary/success/warning/teal
// scales) every other surface in the app draws from — no raw hex. The
// active/highlighted row always overrides this with primary-100 (below),
// same as v1, so keyboard/hover selection stays visually consistent
// regardless of what's selected.
const TYPE_TINT = {
  page: 'bg-gray-100 text-gray-500',
  district: 'bg-primary-50 text-primary-600',
  sector: 'bg-primary-50 text-primary-600',
  model: 'bg-teal-50 text-teal-700',
  alert: 'bg-warning-50 text-warning-700',
  dataset: 'bg-success-50 text-success-700',
}

const GlobalSearch = ({ open, onClose }) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const listRef = useRef(null)

  // Permission-filtered once per user identity, not per keystroke.
  const index = useMemo(() => buildSearchIndex(user), [user])
  const hasQuery = query.trim().length > 0

  // Entity data: districts/sectors come free from data/rwanda.js (no
  // fetch); models/alerts/tables are fetched + cached by
  // useGlobalSearchEntities, debounced off the `open` prop below so
  // toggling the palette never fires more than one fetch per cache window,
  // and keystrokes never trigger a fetch at all.
  const entities = useGlobalSearchEntities(open)
  const entityIndex = useMemo(
    () => buildEntityIndex(user, {
      notebooks: entities.notebooks.data,
      alerts: entities.alerts.data,
      tables: entities.tables.data,
    }),
    [user, entities.notebooks.data, entities.alerts.data, entities.tables.data]
  )

  const canSeeAlerts = hasAnyPermission(user, ENTITY_REQUIRE_ANY.alert)
  const entityLoading = entities.notebooks.loading || entities.tables.loading || (canSeeAlerts && entities.alerts.loading)
  const entityError = entities.notebooks.error || entities.tables.error || (canSeeAlerts && entities.alerts.error)

  // Grouped (page/alert/model/dataset/district/sector) results for an
  // active query — see groupSearchResults for why each type is capped
  // independently instead of one flat top-N (416 sectors can't crowd out
  // a 1-result alert match). `resultRows` flattens groups back into
  // header+item rows in the exact order they render, so keyboard nav can
  // index straight into it the same way v1 indexed into a flat list.
  const groups = useMemo(
    () => (hasQuery ? groupSearchResults(searchCombined(query, { pageIndex: index, entityIndex })) : []),
    [hasQuery, query, index, entityIndex]
  )
  const resultRows = useMemo(() => {
    const rows = []
    let i = 0
    for (const group of groups) {
      rows.push({ kind: 'header', key: `header:${group.type}`, label: group.label })
      for (const item of group.items) {
        rows.push({ kind: 'item', key: item.id, item, index: i })
        i += 1
      }
    }
    return rows
  }, [groups])
  const results = useMemo(() => resultRows.filter((r) => r.kind === 'item').map((r) => r.item), [resultRows])

  const quickLinks = useMemo(() => index.slice(0, QUICK_LINKS_LIMIT), [index])
  const visibleItems = hasQuery ? results : quickLinks

  // Reset to a clean slate every time the palette opens — a stale query
  // from the last visit shouldn't greet the next one.
  useEffect(() => {
    if (open) {
      setQuery('')
      setActiveIndex(0)
    }
  }, [open])

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // Keep the highlighted row in view when the keyboard (not the mouse)
  // drives the selection past the visible scroll area.
  useEffect(() => {
    if (!open) return
    const active = visibleItems[activeIndex]
    if (!active) return
    listRef.current
      ?.querySelector(`#global-search-item-${CSS?.escape ? CSS.escape(active.id) : active.id}`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, visibleItems, open])

  const goTo = (destination) => {
    if (!destination) return
    navigate(destination.path)
    onClose()
  }

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, Math.max(visibleItems.length - 1, 0)))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      goTo(visibleItems[activeIndex])
    }
    // Escape is handled by Modal's own focus trap (useModalFocusTrap), which
    // calls onClose — no local handling needed here.
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="max-w-xl"
      panelClassName="p-0"
      ariaLabelledBy="global-search-heading"
      scrollable={false}
    >
      <div className="flex max-h-[70vh] flex-col">
        <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3.5">
          <Search className="h-5 w-5 shrink-0 text-gray-400" strokeWidth={1.75} />
          <label htmlFor="global-search-input" id="global-search-heading" className="sr-only">
            Search pages, districts, models, alerts, and datasets
          </label>
          <input
            id="global-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search pages, districts, models, alerts, datasets…"
            role="combobox"
            aria-expanded="true"
            aria-autocomplete="list"
            aria-controls="global-search-listbox"
            aria-activedescendant={
              visibleItems[activeIndex] ? `global-search-item-${visibleItems[activeIndex].id}` : undefined
            }
            autoComplete="off"
            spellCheck={false}
            className="flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
          />
          <kbd className="hidden shrink-0 items-center rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 sm:inline-flex">
            Esc
          </kbd>
        </div>

        {hasQuery && (entityLoading || entityError) && (
          <div className="flex items-center gap-1.5 border-b border-gray-100 bg-gray-50/60 px-4 py-1.5 text-[11px] text-gray-500">
            {entityLoading ? (
              <>
                <Loader2 className="h-3 w-3 shrink-0 animate-spin text-gray-400" strokeWidth={2} />
                Searching alerts, models, and datasets…
              </>
            ) : (
              <>
                <AlertTriangle className="h-3 w-3 shrink-0 text-warning-500" strokeWidth={2} />
                Some results (alerts, models, or datasets) couldn&rsquo;t load — showing what&rsquo;s available.
              </>
            )}
          </div>
        )}

        <div
          ref={listRef}
          id="global-search-listbox"
          role="listbox"
          aria-label="Search results"
          className="overflow-y-auto py-2"
        >
          {!hasQuery && visibleItems.length > 0 && (
            <p className="px-4 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Quick links
            </p>
          )}

          {hasQuery && visibleItems.length === 0 && (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
              <div className="rounded-full bg-gray-100 p-3 text-gray-400">
                <Search className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <p className="text-sm font-medium text-gray-700">No matches for &ldquo;{query.trim()}&rdquo;</p>
              <p className="text-xs text-gray-500">
                Try a different name — a page, district, model, alert, or dataset, e.g. &ldquo;schema&rdquo; or &ldquo;gasabo&rdquo;.
              </p>
            </div>
          )}

          {!hasQuery && visibleItems.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-gray-500">Nothing to jump to yet.</div>
          )}

          {!hasQuery &&
            quickLinks.map((destination, i) => {
              const isActive = i === activeIndex
              return (
                <SearchResultRow
                  key={destination.id}
                  destination={destination}
                  isActive={isActive}
                  onHover={() => setActiveIndex(i)}
                  onSelect={() => goTo(destination)}
                />
              )
            })}

          {hasQuery &&
            resultRows.map((row) =>
              row.kind === 'header' ? (
                <p
                  key={row.key}
                  className="px-4 pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400 first:pt-1"
                >
                  {row.label}
                </p>
              ) : (
                <SearchResultRow
                  key={row.key}
                  destination={row.item}
                  isActive={row.index === activeIndex}
                  onHover={() => setActiveIndex(row.index)}
                  onSelect={() => goTo(row.item)}
                />
              )
            )}
        </div>

        <div className="hidden items-center gap-4 border-t border-gray-100 px-4 py-2.5 text-[11px] text-gray-400 sm:flex">
          <span className="inline-flex items-center gap-1.5">
            <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5">↑↓</kbd> Navigate
          </span>
          <span className="inline-flex items-center gap-1.5">
            <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5">Enter</kbd> Select
          </span>
          <span className="inline-flex items-center gap-1.5">
            <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5">Esc</kbd> Close
          </span>
        </div>
      </div>
    </Modal>
  )
}

// One result row — shared by both the "Quick links" (pages only, no query)
// and grouped-by-type (query active) render paths above, so the visual
// language (icon avatar, label/group subtitle, active-row arrow) never
// drifts between the two states. `destination.type` picks the resting
// avatar tint (TYPE_TINT); it's undefined for pre-v2 callers, which falls
// back to the page tint exactly like before.
const SearchResultRow = ({ destination, isActive, onHover, onSelect }) => {
  const Icon = ICONS[destination.icon] || Search
  return (
    <button
      id={`global-search-item-${destination.id}`}
      type="button"
      role="option"
      aria-selected={isActive}
      onMouseEnter={onHover}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors focus:outline-none',
        isActive ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50'
      )}
    >
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          isActive ? 'bg-primary-100 text-primary-600' : (TYPE_TINT[destination.type] || TYPE_TINT.page)
        )}
      >
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{destination.label}</span>
        <span className="block truncate text-xs text-gray-400">{destination.group}</span>
      </span>
      {isActive && <ArrowRight className="h-4 w-4 shrink-0 text-primary-500" strokeWidth={1.75} />}
    </button>
  )
}

export default GlobalSearch
